import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "http://nissi-india.com";

const ContractorPaymentReportPrint = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // ✅ read filters from query (preferred) OR fallback localStorage for branchid
  const branchid =
    Number(query.get("branchid") || 0) ||
    Number(localStorage.getItem("branchId") || 0) ||
    0;

  const fromDate = query.get("fromDate") || query.get("from") || "";
  const toDate = query.get("toDate") || query.get("to") || "";
  const contractorid = Number(query.get("contractorid") || 0);
  const projectid = Number(query.get("projectid") || 0);
  const billno = query.get("billno") || query.get("billNumber") || "";

  // (Optional) for showing header text only (not required by API)
  const branchLabel = query.get("branch") || (branchid ? `Branch ${branchid}` : "");
  const projectLabel = query.get("project") || (projectid ? `Project ${projectid}` : "All Projects");

  const [reportData, setReportData] = useState([]); // list from API
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatMoney = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";
  };

  const totalAmount = useMemo(
    () => reportData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [reportData]
  );

  const fetchReportData = async () => {
    if (!branchid) {
      setError("branchid missing (localStorage.branchId or query ?branchid= )");
      return;
    }
    if (!fromDate || !toDate) {
      setError("fromDate and toDate are required in URL query.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${BASE_URL}/api/contractorReport/printAll`, {
        params: {
          branchid,
          fromDate,
          toDate,
          contractorid,
          projectid,
          billno,
        },
      });

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load print data");
        setReportData([]);
        return;
      }

      const list = res.data.list || [];

      // ✅ map backend columns into your UI columns
      const mapped = list.map((r, idx) => ({
        sno: idx + 1,
        billNumber: r.fld_billno || "",
        date: r.fld_date || "",
        contractorName: r.fld_contractorname || "",
        projectName: r.fld_projectname || "",
        // backend currently doesn't return workTypeName; keep empty or add if you have column
        workTypeName: r.fld_worktypename || r.workTypeName || "",
        amount: Number(r.fld_paidamount ?? 0),
        balAmount: Number(r.fld_balamount ?? 0),
        userName: r.fld_username || "",
        payId: r.fld_contractorpayid,
      }));

      setReportData(mapped);

      // ✅ optional auto print
      setTimeout(() => window.print(), 400);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Server error while loading print data");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid, fromDate, toDate, contractorid, projectid, billno]);

  return (
    <div className="max-w-full mx-auto p-4 bg-white font-sans text-xs print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
          <div className="text-sm">Nissi</div>
          <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
          <div className="text-xs">
            3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,India
          </div>
          <div className="text-xs">
            Phone No : 9003744644 Email : infraconsoft-implementation@nissiinfotech.in
          </div>
        </div>

        <div className="text-center py-2 border-b border-black font-bold">
          CONTRACTOR PAYMENT REPORT
        </div>

        {/* ✅ Filter Header (optional but useful) */}
        <div className="px-3 py-2 border-b border-black text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <b>From :</b> {fromDate}
            </div>
            <div>
              <b>To :</b> {toDate}
            </div>
            <div>
              <b>Branch :</b> {branchLabel || "-"}
            </div>
            <div>
              <b>Project :</b> {projectLabel || "-"}
            </div>
          </div>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div className="mt-1">{error}</div>
          <div className="mt-2 text-xs text-gray-700">
            Debug: branchid={branchid || "-"} | fromDate={fromDate || "-"} | toDate={toDate || "-"}
          </div>
          <button
            onClick={fetchReportData}
            className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black px-1 py-2 w-8">Sno</th>
              <th className="border-r border-black px-1 py-2 w-16">Bill Number</th>
              <th className="border-r border-black px-1 py-2 w-16">Date</th>
              <th className="border-r border-black px-1 py-2 w-20">Contractor Name</th>
              <th className="border-r border-black px-1 py-2 w-24">Project Name</th>
              <th className="border-r border-black px-1 py-2 w-20">Work Type Name</th>
              <th className="border-r border-black px-1 py-2 w-16">Amount</th>
              <th className="border-r border-black px-1 py-2 w-16">Bal Amount</th>
              <th className="border-r border-black px-1 py-2 w-16">User Name</th>
            </tr>
          </thead>
          <tbody>
            {!loading && reportData.length === 0 ? (
              <tr>
                <td colSpan="9" className="p-4 text-center text-gray-500 border-r border-black">
                  No data available for the selected criteria
                </td>
              </tr>
            ) : (
              reportData.map((item, index) => (
                <tr key={index} className="border-b border-gray-300">
                  <td className="border-r border-black px-1 py-1 text-center">{item.sno}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.billNumber}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.date}</td>
                  <td className="border-r border-black px-1 py-1">{item.contractorName}</td>
                  <td className="border-r border-black px-1 py-1">{item.projectName}</td>
                  <td className="border-r border-black px-1 py-1">{item.workTypeName}</td>
                  <td className="border-r border-black px-1 py-1 text-right">{formatMoney(item.amount)}</td>
                  <td className="border-r border-black px-1 py-1 text-right">{formatMoney(item.balAmount)}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.userName}</td>
                </tr>
              ))
            )}

            {/* Total */}
            {reportData.length > 0 && (
              <tr className="border-t-2 border-black font-bold">
                <td colSpan="6" className="border-r border-black px-1 py-1 text-right">
                  Total:
                </td>
                <td className="border-r border-black px-1 py-1 text-right">{formatMoney(totalAmount)}</td>
                <td colSpan="2" className="px-1 py-1"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Page number */}
      <div className="text-right mt-2 text-xs">1</div>

      {/* Footer */}
      <div className="mt-4 text-xs text-gray-600 border-t border-gray-300 pt-2">
        <div className="underline">
          Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default ContractorPaymentReportPrint;
